import { DeleteIcon, EditIcon } from "@chakra-ui/icons";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  Heading,
  HStack,
  Spinner,
  Text,
  Tooltip,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import type { Geometry } from "geojson";
import { useRef, useState } from "react";
import { RiDownloadLine, RiUploadLine } from "react-icons/ri";
import { Link } from "react-router-dom";
import { EmptyState } from "src/components/EmptyState";
import { GenericError } from "src/components/Feedback/GenericError";
import { SearchInput } from "src/components/Inputs/SearchInput";
import { RemoteDataLoadingIndicator } from "src/components/Layout/RemoteData/RemoteDataLoadingIndicator";
import * as FSQ from "src/generated/hooks/form-service/queries";
import * as FSR from "src/generated/hooks/form-service/requests";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useGlossary } from "src/hooks/useGlossary";
import { useOrganization } from "src/hooks/useOrganization";
import LocationOnMapSvg from "src/images/location-on-map.svg?react";
import * as Url from "src/services/url";
import { ENROLLMENT_PERIOD_TAB_HASH } from "src/services/url/OrgAdmin";
import * as RD from "src/types/remoteData";
import { ViewSchoolBoundaries } from "../../enrollmentPeriods/EnrollmentSchoolsTab/components/ViewSchoolBoundaries";
import { EditBoundaryDrawer } from "./EditBoundaryDrawer";
import { ImportBoundariesDrawer } from "./ImportBoundariesDrawer";

type Boundary = FSR.GetBoundariesResponse["data"]["boundaries"][number];
type BoundaryConnection =
  FSR.GetBoundariesResponse["data"]["boundaries"][number]["school_boundaries"][number];

const getBoundaryName = (boundary: Boundary): string => {
  return boundary.key || `Boundary ${boundary.id}`;
};

const sortAndDeduplicateConnections = (
  connections: BoundaryConnection[]
): BoundaryConnection[] =>
  Array.from(
    new Map(
      connections.map((sb) => [
        `${(sb.school?.id ?? "") + (sb.enrollment_period?.id ?? "")}`,
        sb,
      ])
    ).values()
  ).sort(
    (a, b) =>
      (a.enrollment_period?.name ?? "").localeCompare(
        b.enrollment_period?.name ?? ""
      ) || (a.school?.name ?? "").localeCompare(b.school?.name ?? "")
  );

export const BoundariesList = () => {
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [boundaryToDelete, setBoundaryToDelete] = useState<string | null>(null);
  const [importDrawerOpen, setImportDrawerOpen] = useState(false);
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [boundaryToEdit, setBoundaryToEdit] = useState<Boundary | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const organization = useOrganization();
  const { glossary } = useGlossary();
  const toast = useAvelaToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);

  const boundariesQuery = FSQ.useGetBoundaries(
    organization.hasData()
      ? { query: { organization_id: organization.data.id } }
      : {},
    [],
    { enabled: organization.hasData() }
  );

  const deleteBoundaryMutation = FSQ.useDeleteBoundaries();

  const boundariesRemoteData = RD.fromRestResult(boundariesQuery);
  const boundaries = boundariesRemoteData.hasData()
    ? boundariesRemoteData.data.data?.boundaries || []
    : [];
  const hasBoundaries = boundaries.length > 0;
  const refetchBoundaries = boundariesQuery.refetch;

  const filteredBoundaries = (
    searchQuery.trim()
      ? boundaries.filter((boundary: Boundary) => {
          const name = getBoundaryName(boundary).toLowerCase();
          const id = boundary.id.toLowerCase();
          const query = searchQuery.toLowerCase().trim();

          return name.includes(query) || id.includes(query);
        })
      : [...boundaries]
  ).sort((a, b) =>
    getBoundaryName(a).localeCompare(getBoundaryName(b), undefined, {
      sensitivity: "base",
    })
  );

  const handleDeleteClick = (boundaryId: string) => {
    setBoundaryToDelete(boundaryId);
    onOpen();
  };

  const handleEditClick = (boundary: Boundary) => {
    setBoundaryToEdit(boundary);
    setEditDrawerOpen(true);
  };

  const handleExportBoundaries = async () => {
    setIsExporting(true);
    try {
      const response = await FSR.exportBoundariesByOrgGeoJson({
        path: {
          orgId: organization.map((org) => org.id).toNullable() || "",
        },
      });

      if (response.data?.data) {
        // Create a download link for the exported geoJSON data
        const blob = new Blob([JSON.stringify(response.data.data, null, 2)], {
          type: "application/json",
        });
        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.download = `boundaries-export-${
          new Date().toISOString().split("T")[0]
        }.geojson`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        toast({
          title: "Success",
          description: "Boundaries exported successfully",
          status: "success",
          duration: 3000,
        });
      }
    } catch (error) {
      console.error("Error exporting boundaries:", error);
      toast({
        title: "Error",
        description: "Failed to export boundaries",
        status: "error",
        duration: 5000,
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!boundaryToDelete) return;

    try {
      await deleteBoundaryMutation.mutateAsync({
        body: {
          ids: [boundaryToDelete],
        },
      });

      toast({
        title: "Success",
        description: "Boundary deleted successfully",
        status: "success",
        duration: 3000,
      });

      refetchBoundaries();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete boundary",
        status: "error",
        duration: 5000,
      });
    } finally {
      setBoundaryToDelete(null);
      onClose();
    }
  };

  if (boundariesRemoteData.isLoading() || boundariesRemoteData.isNotAsked()) {
    return (
      <RemoteDataLoadingIndicator
        remoteData={boundariesRemoteData}
        loadingElement={<Spinner size="lg" />}
      />
    );
  }

  if (boundariesRemoteData.hasError()) {
    return <GenericError message="Unable to load boundaries." />;
  }

  if (boundariesRemoteData.hasData() && !hasBoundaries) {
    return (
      <>
        <Heading as="h1" variant="admin">
          Boundaries
        </Heading>
        <Flex align="center" justify="center" flex="1" w="100%">
          <EmptyState
            heading="No boundaries"
            description="Organize schools' boundaries with location polygons"
            Svg={LocationOnMapSvg}
            size="lg"
          >
            <Button
              leftIcon={<RiUploadLine />}
              variant="outline"
              size="md"
              colorScheme="primary"
              onClick={() => setImportDrawerOpen(true)}
            >
              Import boundaries
            </Button>
          </EmptyState>
        </Flex>

        <ImportBoundariesDrawer
          onBoundaryImported={() => refetchBoundaries()}
          isOpen={importDrawerOpen}
          onClose={() => setImportDrawerOpen(false)}
        />
      </>
    );
  }

  return (
    <>
      <VStack spacing={6} align="stretch" w="100%">
        <Flex justify="space-between" align="center">
          <Heading as="h1" variant="admin">
            Boundaries
          </Heading>
          <HStack spacing={2}>
            <Button
              leftIcon={<RiUploadLine />}
              variant="ghost"
              size="md"
              onClick={() => setImportDrawerOpen(true)}
            >
              Import boundaries
            </Button>
            <Button
              leftIcon={<RiDownloadLine />}
              variant="outline"
              onClick={handleExportBoundaries}
              isLoading={isExporting}
            >
              Export boundaries
            </Button>
          </HStack>
        </Flex>

        <Box w="100%">
          <SearchInput
            placeholder="Search boundaries..."
            value={searchQuery}
            onSearchChange={setSearchQuery}
            onClear={() => setSearchQuery("")}
            variant="leftIcon"
          />
        </Box>

        <VStack spacing={4} align="stretch">
          {filteredBoundaries.map((boundary: Boundary) => (
            <Card key={boundary.id} variant="outline">
              <CardBody>
                <VStack align="stretch" spacing={3}>
                  <Flex justify="space-between" align="center">
                    <VStack align="start" spacing={1}>
                      <Text fontWeight="semibold" fontSize="lg">
                        {getBoundaryName(boundary)}
                      </Text>
                      <Text fontSize="sm" color="gray.500">
                        {boundary.id}
                      </Text>
                      {boundary.school_boundaries.length > 0 && (
                        <VStack align="start" spacing={0} mt={2}>
                          <Text fontSize="sm" fontWeight="semibold">
                            Used in
                          </Text>
                          <Box>
                            {sortAndDeduplicateConnections(
                              boundary.school_boundaries
                            ).map((sb) => (
                              <Text key={sb.id} fontSize="sm">
                                {sb.enrollment_period?.id &&
                                organization.hasData() ? (
                                  <Link
                                    to={`${Url.OrgAdmin.Enrollments.edit(
                                      organization.data,
                                      sb.enrollment_period.id
                                    )}?school_filter=${encodeURIComponent(
                                      sb.school?.name || ""
                                    )}#${ENROLLMENT_PERIOD_TAB_HASH.SCHOOLS}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                      textDecoration: "underline",
                                    }}
                                  >
                                    {sb.enrollment_period?.name ||
                                      "Unknown Period"}
                                    ,{" "}
                                    {sb.school?.name ||
                                      glossary`Unknown School`}
                                  </Link>
                                ) : (
                                  <>
                                    {sb.enrollment_period?.name ||
                                      "Unknown Period"}
                                    ,{" "}
                                    {sb.school?.name ||
                                      glossary`Unknown School`}
                                  </>
                                )}
                              </Text>
                            ))}
                          </Box>
                        </VStack>
                      )}
                    </VStack>
                    <HStack spacing={2}>
                      <Tooltip
                        label="To delete this boundary, remove it from enrollment schools using it"
                        isDisabled={boundary.school_boundaries_count === 0}
                        hasArrow
                        placement="top"
                        textAlign="center"
                      >
                        <Button
                          leftIcon={<DeleteIcon />}
                          variant="outline"
                          size="sm"
                          color="black"
                          borderColor="gray.300"
                          onClick={() => handleDeleteClick(boundary.id)}
                          isLoading={deleteBoundaryMutation.isPending}
                          isDisabled={boundary.school_boundaries_count > 0}
                        >
                          Delete
                        </Button>
                      </Tooltip>
                      <Button
                        leftIcon={<EditIcon />}
                        variant="outline"
                        size="sm"
                        color="black"
                        borderColor="gray.300"
                        onClick={() => handleEditClick(boundary)}
                      >
                        Edit
                      </Button>
                    </HStack>
                  </Flex>
                  <ViewSchoolBoundaries
                    boundaries={[
                      {
                        geometry: boundary.geom as Geometry,
                        name: boundary.name,
                      },
                    ]}
                  />
                </VStack>
              </CardBody>
            </Card>
          ))}
        </VStack>
      </VStack>

      <ImportBoundariesDrawer
        onBoundaryImported={() => refetchBoundaries()}
        isOpen={importDrawerOpen}
        onClose={() => setImportDrawerOpen(false)}
      />

      <EditBoundaryDrawer
        boundary={boundaryToEdit}
        onBoundaryUpdated={() => refetchBoundaries()}
        isOpen={editDrawerOpen}
        onClose={() => {
          setEditDrawerOpen(false);
          setBoundaryToEdit(null);
        }}
      />

      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete boundary?
            </AlertDialogHeader>

            <AlertDialogBody>
              Deleting this boundary will remove any location association with
              schools and eligibility. It can't be undone or recovered.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button
                variant="outline"
                border="0px"
                ref={cancelRef}
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                colorScheme="primary"
                onClick={handleDeleteConfirm}
                ml={3}
                isLoading={deleteBoundaryMutation.isPending}
              >
                Yes, delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};
